<template>
  <div class="bacc-game">
    <!-- Game Topbar -->
    <div class="game-topbar">
      <button class="btn-back" @click="router.push({ name: 'player.lobby' })">{{ $t('games.back_to_casino') }}</button>
      <span class="game-title-bar">
        <AppIcon name="game-baccarat" :size="18" /> {{ $t('games.baccarat.title') }}
        <span class="game-tag bacc-tag">{{ $t('games.baccarat.tag') }}</span>
      </span>
      <div class="game-topbar-actions">
        <button class="icon-btn" :title="$t('games.provably_fair')" @click="router.push({ name: 'player.provably-fair' })">
          <AppIcon name="provably-fair" :size="16" />
        </button>
      </div>
    </div>

    <!-- Game Body -->
    <div class="game-body">
      <!-- ── Table Area ──────────────────────────────────────────────── -->
      <div class="bacc-canvas-area">
        <div class="canvas-bg-grid"></div>

        <!-- Commission badge -->
        <div class="commission-badge">{{ $t('games.baccarat.commission_notice') }}</div>

        <!-- Table felt -->
        <div class="bacc-felt">
          <!-- Banker Zone (top) -->
          <div class="bacc-zone banker-zone" :class="{ winner: roundDone && winnerSide === 'banker' }">
            <div class="zone-header">
              <span class="zone-icon">🏦</span>
              <span class="zone-label">{{ $t('games.baccarat.banker') }}</span>
              <div class="hand-value-badge banker-badge">
                {{ squeezeBankerMasked.length > 0 ? '?' : bankerValue }}
              </div>
            </div>
            <div class="cards-area">
              <TransitionGroup name="card-deal" tag="div" class="cards-row">
                <div
                  v-for="(card, idx) in animBankerCards"
                  :key="`banker-${card.rank}-${card.suit}-${idx}`"
                  class="playing-card"
                  :class="[
                    isRedSuit(card.suit) ? 'red' : 'black',
                    { 'third-card': idx === 2 },
                  ]"
                >
                  <div class="card-corner tl">
                    <span class="card-rank">{{ rankLabel(card.rank) }}</span>
                    <span class="card-suit-small">{{ suitSymbol(card.suit) }}</span>
                  </div>
                  <div class="card-center-suit">{{ suitSymbol(card.suit) }}</div>
                  <div class="card-corner br">
                    <span class="card-rank">{{ rankLabel(card.rank) }}</span>
                    <span class="card-suit-small">{{ suitSymbol(card.suit) }}</span>
                  </div>
                  <!-- Squeeze mask: hides card face until squeezed open -->
                  <div
                    v-if="squeezeEnabled && squeezeBankerMasked.includes(idx)"
                    class="squeeze-mask"
                    :class="{ animating: squeezeAnimating, skip: squeezeSkipped }"
                    @click="skipSqueeze"
                  />
                </div>
              </TransitionGroup>
            </div>
          </div>

          <!-- ── Betting Table (chip-placement zones) ─────────────────── -->
          <div class="betting-table" :class="{ disabled: isDealing }">
            <div
              v-for="zoneKey in ZONE_ORDER"
              :key="zoneKey"
              class="bet-zone"
              :class="[`zone-${zoneKey.replace('_', '-')}`, { funded: zoneTotal(zoneKey) > 0 }]"
              :title="`${ZONE_CONFIGS[zoneKey].label} — ${ZONE_CONFIGS[zoneKey].odds}`"
              @click="placeChipOnZone(zoneKey)"
            >
              <div class="bz-info">
                <span class="bz-label">{{ ZONE_CONFIGS[zoneKey].label }}</span>
                <span class="bz-odds">{{ ZONE_CONFIGS[zoneKey].odds }}</span>
              </div>
              <div class="bz-chip-area">
                <Transition name="chip-pop">
                  <div
                    v-if="zoneTotal(zoneKey) > 0"
                    class="bz-chip"
                    :class="`chip-color-${ZONE_CONFIGS[zoneKey].color}`"
                  >
                    <span class="bz-chip-val">${{ formatZoneAmount(zoneTotal(zoneKey)) }}</span>
                  </div>
                  <div v-else class="bz-empty-ring"></div>
                </Transition>
              </div>
            </div>
          </div>

          <!-- Divider with road map -->
          <div class="road-strip">
            <div
              v-for="(entry, i) in history"
              :key="i"
              class="road-dot"
              :class="entry.outcome"
              :title="outcomeLabel(entry.outcome)"
            >
              {{ outcomeInitial(entry.outcome) }}
            </div>
          </div>

          <!-- Player Zone (bottom) -->
          <div class="bacc-zone player-zone" :class="{ winner: roundDone && winnerSide === 'player' }">
            <div class="zone-header">
              <span class="zone-icon">👤</span>
              <span class="zone-label">{{ $t('games.baccarat.player') }}</span>
              <div class="hand-value-badge player-badge">
                {{ squeezePlayerMasked.length > 0 ? '?' : playerValue }}
              </div>
            </div>
            <div class="cards-area">
              <TransitionGroup name="card-deal" tag="div" class="cards-row">
                <div
                  v-for="(card, idx) in animPlayerCards"
                  :key="`player-${card.rank}-${card.suit}-${idx}`"
                  class="playing-card"
                  :class="[
                    isRedSuit(card.suit) ? 'red' : 'black',
                    { 'third-card': idx === 2 },
                  ]"
                >
                  <div class="card-corner tl">
                    <span class="card-rank">{{ rankLabel(card.rank) }}</span>
                    <span class="card-suit-small">{{ suitSymbol(card.suit) }}</span>
                  </div>
                  <div class="card-center-suit">{{ suitSymbol(card.suit) }}</div>
                  <div class="card-corner br">
                    <span class="card-rank">{{ rankLabel(card.rank) }}</span>
                    <span class="card-suit-small">{{ suitSymbol(card.suit) }}</span>
                  </div>
                  <!-- Squeeze mask -->
                  <div
                    v-if="squeezeEnabled && squeezePlayerMasked.includes(idx)"
                    class="squeeze-mask"
                    :class="{ animating: squeezeAnimating, skip: squeezeSkipped }"
                    @click="skipSqueeze"
                  />
                </div>
              </TransitionGroup>
            </div>
          </div>
        </div>

        <!-- Skip Squeeze Button -->
        <Transition name="result-fade">
          <button v-if="squeezeAnimating" class="btn-skip-squeeze" @click="skipSqueeze">
            {{ $t('games.baccarat.squeeze') }} — Click to reveal
          </button>
        </Transition>

        <!-- Result Overlay -->
        <Transition name="result-fade">
          <div v-if="resultVisible && roundResult !== null" class="result-overlay">
            <div
              class="result-text"
              :class="{
                player: roundResult.outcome === 'player',
                banker: roundResult.outcome === 'banker',
                tie: roundResult.outcome === 'tie',
              }"
            >
              {{ resultHeading }}
            </div>
            <div class="result-scores">
              <span class="score-badge" :class="{ win: roundResult.outcome === 'player' }">
                Player {{ roundResult.player_value }}
              </span>
              <span class="score-vs">vs</span>
              <span class="score-badge" :class="{ win: roundResult.outcome === 'banker' }">
                Banker {{ roundResult.banker_value }}
              </span>
            </div>
            <div
              class="result-amount"
              :class="{ win: isWin, loss: isLoss }"
            >
              {{ resultAmountText }}
            </div>
            <div v-if="naturalLabel" class="result-natural">{{ naturalLabel }}</div>
            <button class="btn-play-again" @click="resetRound">{{ $t('games.play_again') }}</button>
          </div>
        </Transition>
      </div>

      <!-- ── Sidebar ────────────────────────────────────────────────── -->
      <div class="game-sidebar">
        <div class="game-tabs">
          <button
            class="game-tab"
            :class="{ active: activeTab === 'bet' }"
            @click="activeTab = 'bet'"
          >
            {{ $t('games.bet_tab') }}
          </button>
          <button
            class="game-tab"
            :class="{ active: activeTab === 'roads' }"
            @click="activeTab = 'roads'"
          >
            Roads
          </button>
          <button
            class="game-tab"
            :class="{ active: activeTab === 'bets' }"
            @click="activeTab = 'bets'"
          >
            History
          </button>
          <button
            class="game-tab"
            :class="{ active: activeTab === 'rules' }"
            @click="activeTab = 'rules'"
          >
            {{ $t('games.rules_tab') }}
          </button>
        </div>

        <!-- BET TAB -->
        <div v-if="activeTab === 'bet'" class="tab-content">
          <!-- ── Chip denomination selector ── -->
          <span class="ctrl-label">Select Chip</span>
          <div class="chip-row">
            <button
              v-for="chip in CHIPS"
              :key="chip.cents"
              class="chip"
              :class="[chip.className, { selected: selectedChipCents === chip.cents }]"
              :disabled="isDealing"
              @click="selectedChipCents = chip.cents"
            >
              {{ chip.label }}
            </button>
          </div>

          <!-- ── Action buttons: Undo / Clear / Rebet ── -->
          <div class="bet-action-row">
            <button
              class="btn-bet-action"
              :disabled="isDealing || betHistory.length === 0"
              title="Undo last chip"
              @click="undoLastBet"
            >
              Undo
            </button>
            <button
              class="btn-bet-action"
              :disabled="isDealing || totalBetCents === 0"
              title="Clear all bets"
              @click="clearBets"
            >
              Clear
            </button>
            <button
              class="btn-bet-action"
              :disabled="isDealing || savedBets.size === 0"
              title="Repeat last round's bets"
              @click="rebet"
            >
              Rebet
            </button>
          </div>

          <div class="ctrl-divider"></div>

          <!-- ── Per-zone bet amounts ── -->
          <span class="ctrl-label">Current Bets</span>
          <div class="zone-bet-list">
            <div
              v-for="zoneKey in ZONE_ORDER"
              :key="zoneKey"
              class="zone-bet-row"
              :class="[`zbr-${zoneKey.replace('_', '-')}`, { funded: zoneTotal(zoneKey) > 0 }]"
            >
              <span class="zbr-name">{{ ZONE_CONFIGS[zoneKey].label }}</span>
              <span class="zbr-odds">{{ ZONE_CONFIGS[zoneKey].odds }}</span>
              <span class="zbr-amount" :class="{ active: zoneTotal(zoneKey) > 0 }">
                {{ zoneTotal(zoneKey) > 0 ? '$' + (zoneTotal(zoneKey) / 100).toFixed(2) : '—' }}
              </span>
            </div>
          </div>

          <!-- ── Min/Max info ── -->
          <div class="limit-bar">
            <span class="lb-item">Min: ${{ (MIN_BET_CENTS / 100).toFixed(0) }}</span>
            <span class="lb-sep">·</span>
            <span class="lb-item">Main max: ${{ (MAX_BET_MAIN_CENTS / 100).toFixed(0) }}</span>
            <span class="lb-sep">·</span>
            <span class="lb-item">Pair max: ${{ (MAX_BET_PAIR_CENTS / 100).toFixed(0) }}</span>
          </div>

          <!-- Stats -->
          <div class="stat-pills">
            <div class="stat-pill">
              <div class="sp-label">Total Bet</div>
              <div class="sp-value">${{ (totalBetCents / 100).toFixed(2) }}</div>
            </div>
            <div class="stat-pill">
              <div class="sp-label">Potential Win</div>
              <div class="sp-value gold">${{ (potentialWinCents / 100).toFixed(2) }}</div>
            </div>
          </div>

          <!-- Warnings -->
          <div v-if="insufficientBalance" class="warn-msg">
            {{ $t('games.insufficient_balance') }}
          </div>

          <!-- Error -->
          <div v-if="errorMsg" class="error-msg">{{ errorMsg }}</div>

          <!-- Deal button -->
          <button
            class="btn-action"
            :disabled="!canDeal"
            @click="placeBet"
          >
            {{ isDealing ? 'Dealing...' : 'DEAL' }}
          </button>

          <!-- Balance -->
          <div class="balance-row">
            <span class="bal-label">Balance</span>
            <span class="bal-value green">{{ balanceDisplay }}</span>
          </div>

          <!-- Squeeze toggle -->
          <div class="squeeze-toggle-row">
            <div class="squeeze-toggle-info">
              <span class="sq-label">Squeeze Mode</span>
              <span class="sq-desc">Slow card reveal animation</span>
            </div>
            <button
              class="sq-toggle"
              :class="{ active: squeezeEnabled }"
              :disabled="isDealing"
              @click="squeezeEnabled = !squeezeEnabled"
            >
              {{ squeezeEnabled ? 'ON' : 'OFF' }}
            </button>
          </div>
        </div>

        <!-- ROADS TAB -->
        <div v-if="activeTab === 'roads'" class="tab-content">
          <div class="roads-header">
            <span class="ctrl-label">Scoreboards</span>
            <button class="btn-clear-roads" :disabled="history.length === 0" @click="clearHistory">
              Clear
            </button>
          </div>

          <!-- Bead Road (10 cols × 5 rows = 50 results) -->
          <div class="road-section">
            <div class="road-title">
              Bead Road <span class="road-subtitle">(last 50)</span>
            </div>
            <div v-if="history.length > 0" class="bead-road-grid">
              <template v-for="(row, rowIdx) in beadRoadRows" :key="rowIdx">
                <div
                  v-for="(cell, colIdx) in row"
                  :key="`bead-${rowIdx}-${colIdx}`"
                  class="bead-cell"
                  :class="cell.outcome"
                  :title="`${outcomeLabel(cell.outcome)} — P:${cell.player_value} B:${cell.banker_value}`"
                >
                  {{ outcomeInitial(cell.outcome) }}
                </div>
              </template>
            </div>
            <div v-else class="road-empty">No rounds played yet</div>
          </div>

          <div class="ctrl-divider"></div>

          <!-- Big Road -->
          <div class="road-section">
            <div class="road-title">Big Road</div>
            <div v-if="bigRoadColumns.length > 0" class="big-road-container">
              <div
                v-for="(col, colIdx) in bigRoadColumns"
                :key="colIdx"
                class="big-road-col"
              >
                <div
                  v-for="(cell, rowIdx) in col"
                  :key="rowIdx"
                  class="big-road-cell"
                  :class="cell.side"
                  :title="`${cell.side === 'player' ? 'Player' : 'Banker'}${cell.tieAfterCount > 0 ? ` + ${cell.tieAfterCount} Tie(s)` : ''}`"
                >
                  <span class="br-initial">{{ cell.side === 'player' ? 'P' : 'B' }}</span>
                  <span v-if="cell.tieAfterCount > 0" class="br-tie-count">
                    {{ cell.tieAfterCount }}
                  </span>
                </div>
              </div>
            </div>
            <div v-else class="road-empty">No rounds played yet</div>
          </div>

          <div class="ctrl-divider"></div>

          <!-- Summary stats -->
          <div class="road-stats">
            <div class="rs-item player">
              <div class="rs-dot player-dot">P</div>
              <div class="rs-count">{{ stats.playerWins }}</div>
            </div>
            <div class="rs-item banker">
              <div class="rs-dot banker-dot">B</div>
              <div class="rs-count">{{ stats.bankerWins }}</div>
            </div>
            <div class="rs-item tie">
              <div class="rs-dot tie-dot">T</div>
              <div class="rs-count">{{ stats.ties }}</div>
            </div>
            <div class="rs-item total">
              <div class="rs-label">Rounds</div>
              <div class="rs-count">{{ stats.total }}</div>
            </div>
          </div>

          <div class="road-disclaimer">
            Past results do not predict future outcomes. Please gamble responsibly.
          </div>
        </div>

        <!-- HISTORY TAB -->
        <div v-if="activeTab === 'bets'" class="tab-content">
          <div class="history-stats">
            <div class="stat-pill">
              <div class="sp-label">Player Wins</div>
              <div class="sp-value">{{ stats.playerWins }}</div>
            </div>
            <div class="stat-pill">
              <div class="sp-label">Banker Wins</div>
              <div class="sp-value">{{ stats.bankerWins }}</div>
            </div>
            <div class="stat-pill">
              <div class="sp-label">Ties</div>
              <div class="sp-value">{{ stats.ties }}</div>
            </div>
            <div class="stat-pill">
              <div class="sp-label">Rounds</div>
              <div class="sp-value">{{ stats.total }}</div>
            </div>
          </div>
          <div class="ctrl-divider"></div>
          <div class="history-list">
            <div
              v-for="(entry, i) in [...history].reverse()"
              :key="i"
              class="history-row"
              :class="entry.outcome"
            >
              <span class="hr-round">#{{ history.length - i }}</span>
              <span class="hr-outcome">{{ outcomeLabel(entry.outcome) }}</span>
              <span class="hr-scores">P:{{ entry.player_value }} B:{{ entry.banker_value }}</span>
            </div>
            <div v-if="history.length === 0" class="history-empty">No rounds played yet</div>
          </div>
        </div>

        <!-- RULES TAB -->
        <div v-if="activeTab === 'rules'" class="tab-content">
          <div class="rules-section">
            <div class="rules-title"><span><AppIcon name="game-baccarat" :size="18" /></span> How to Play</div>
            <ul class="rules-list">
              <li class="rules-item">Select chip denomination, then click a betting zone on the table</li>
              <li class="rules-item">Chips accumulate — click the same zone multiple times</li>
              <li class="rules-item">Cards are dealt automatically per fixed tableau rules</li>
              <li class="rules-item">Hand closest to 9 wins (sum mod 10)</li>
              <li class="rules-item">A=1, 2–9=face value, 10/J/Q/K=0</li>
              <li class="rules-item">Natural (8 or 9 on first two cards) — no third card</li>
              <li class="rules-item">Banker win pays 0.95:1 (5% commission)</li>
              <li class="rules-item">Tie pays 8:1</li>
              <li class="rules-item">Player/Banker Pair pays 11:1</li>
            </ul>
          </div>
          <div class="fair-tag"><AppIcon name="provably-fair" :size="12" /> Provably Fair Game</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'
import { useWalletStore } from '@/stores/wallet'
import AppIcon from '@/components/AppIcon.vue'

useI18n()
const router = useRouter()
const walletStore = useWalletStore()

// ─── Types ────────────────────────────────────────────────────────────────────

type BetZone = 'player' | 'banker' | 'tie' | 'player_pair' | 'banker_pair'

interface Card {
  rank: number
  suit: string
  deck?: number
}

interface HistoryEntry {
  outcome: 'player' | 'banker' | 'tie'
  player_value: number
  banker_value: number
}

interface RoundResult {
  outcome: 'player' | 'banker' | 'tie'
  player_value: number
  banker_value: number
  is_natural: boolean
  player_cards: Card[]
  banker_cards: Card[]
  player_third_card: Card | null
  banker_third_card: Card | null
}

interface ChipConfig {
  cents: number
  label: string
  className: string
}

interface ZoneConfig {
  label: string
  odds: string
  color: string
  max: number
}

interface BetHistoryEntry {
  zone: BetZone
  chipCents: number
}

interface BigRoadCell {
  side: 'player' | 'banker'
  tieAfterCount: number
}

type BigRoadColumn = BigRoadCell[]

// ─── Constants ────────────────────────────────────────────────────────────────

const CHIPS: ChipConfig[] = [
  { cents: 100, label: '$1', className: 'chip-1' },
  { cents: 500, label: '$5', className: 'chip-5' },
  { cents: 2500, label: '$25', className: 'chip-25' },
  { cents: 10000, label: '$100', className: 'chip-100' },
  { cents: 50000, label: '$500', className: 'chip-500' },
]

const MIN_BET_CENTS = 100 // $1
const MAX_BET_MAIN_CENTS = 100_000 // $1000 for player/banker/tie
const MAX_BET_PAIR_CENTS = 50_000 // $500 for pairs

const ZONE_ORDER: BetZone[] = ['player_pair', 'player', 'tie', 'banker', 'banker_pair']

const ZONE_CONFIGS: Record<BetZone, ZoneConfig> = {
  player: { label: 'Player', odds: '1:1', color: 'blue', max: MAX_BET_MAIN_CENTS },
  banker: { label: 'Banker', odds: '0.95:1', color: 'red', max: MAX_BET_MAIN_CENTS },
  tie: { label: 'Tie', odds: '8:1', color: 'green', max: MAX_BET_MAIN_CENTS },
  player_pair: { label: 'P-Pair', odds: '11:1', color: 'blue-light', max: MAX_BET_PAIR_CENTS },
  banker_pair: { label: 'B-Pair', odds: '11:1', color: 'red-light', max: MAX_BET_PAIR_CENTS },
}

// Potential win multipliers (total return / bet)
const ZONE_MULTIPLIERS: Record<BetZone, number> = {
  player: 2,
  banker: 1.95,
  tie: 9,
  player_pair: 12,
  banker_pair: 12,
}

const DEAL_DELAY_MS = 320
const THIRD_CARD_DELAY_MS = 600

// ─── Reactive state ───────────────────────────────────────────────────────────

const activeTab = ref<'bet' | 'bets' | 'roads' | 'rules'>('bet')
const isDealing = ref(false)
const errorMsg = ref<string | null>(null)

// Chip-placement bet state
const selectedChipCents = ref<number>(CHIPS[0].cents)
const zoneBets = ref<Map<BetZone, number>>(new Map())
const betHistory = ref<BetHistoryEntry[]>([])
const savedBets = ref<Map<BetZone, number>>(new Map())

// Round state
const animPlayerCards = ref<Card[]>([])
const animBankerCards = ref<Card[]>([])
const playerValue = ref<number>(0)
const bankerValue = ref<number>(0)
const roundDone = ref(false)
const winnerSide = ref<'player' | 'banker' | 'tie' | null>(null)
const roundResult = ref<RoundResult | null>(null)
const resultVisible = ref(false)
const payoutCents = ref(0)
const totalBetPaidCents = ref(0)

// History
const history = ref<HistoryEntry[]>([])
const stats = ref({ playerWins: 0, bankerWins: 0, ties: 0, total: 0 })

// Squeeze feature
const squeezeEnabled = ref(false)
const squeezePlayerMasked = ref<number[]>([]) // indices of player cards showing the back mask
const squeezeBankerMasked = ref<number[]>([]) // indices of banker cards showing the back mask
const squeezeAnimating = ref(false) // true while the squeeze CSS animation is playing
const squeezeSkipped = ref(false) // true when player clicks skip (triggers CSS skip class)

// Roadmap computed properties
const beadRoadRows = computed<HistoryEntry[][]>(() => {
  const COLS = 10
  const rows: HistoryEntry[][] = []
  history.value.slice(0, 50).forEach((entry, i) => {
    const rowIdx = Math.floor(i / COLS)
    if (!rows[rowIdx]) rows[rowIdx] = []
    rows[rowIdx].push(entry)
  })
  return rows
})

const bigRoadColumns = computed<BigRoadColumn[]>(() => {
  const columns: BigRoadColumn[] = []
  let currentCol: BigRoadColumn = []
  let lastSide: 'player' | 'banker' | null = null

  for (const entry of history.value) {
    if (entry.outcome === 'tie') {
      if (currentCol.length > 0) {
        currentCol[currentCol.length - 1].tieAfterCount++
      }
      continue
    }

    const side = entry.outcome as 'player' | 'banker'

    if (lastSide === null || side !== lastSide) {
      // Side changed or first entry — start a new column
      if (currentCol.length > 0) {
        columns.push(currentCol)
      }
      currentCol = []
      lastSide = side
    } else if (currentCol.length >= 6) {
      // Column full — wrap to new column with same side
      columns.push(currentCol)
      currentCol = []
    }

    currentCol.push({ side, tieAfterCount: 0 })
  }

  if (currentCol.length > 0) {
    columns.push(currentCol)
  }

  return columns
})

// ─── Computed ─────────────────────────────────────────────────────────────────

const balanceCents = computed(() => {
  const bal = walletStore.balance?.balance ?? 0
  return Math.round(bal * 100)
})

const balanceDisplay = computed(() => '$' + (balanceCents.value / 100).toFixed(2))

const totalBetCents = computed(() => {
  let total = 0
  for (const amount of zoneBets.value.values()) total += amount
  return total
})

const potentialWinCents = computed(() => {
  let total = 0
  for (const [zone, amount] of zoneBets.value.entries()) {
    total += Math.floor(amount * ZONE_MULTIPLIERS[zone])
  }
  return total
})

const insufficientBalance = computed(
  () => totalBetCents.value > 0 && balanceCents.value > 0 && totalBetCents.value > balanceCents.value,
)

const canDeal = computed(
  () =>
    !isDealing.value &&
    !roundDone.value &&
    totalBetCents.value >= MIN_BET_CENTS &&
    !insufficientBalance.value,
)

const isWin = computed(() => payoutCents.value > totalBetPaidCents.value)
const isLoss = computed(() => payoutCents.value === 0)

const resultHeading = computed(() => {
  if (!roundResult.value) return ''
  switch (roundResult.value.outcome) {
    case 'player':
      return '👤 PLAYER WINS'
    case 'banker':
      return '🏦 BANKER WINS'
    case 'tie':
      return '🤝 TIE'
    default:
      return ''
  }
})

const resultAmountText = computed(() => {
  const net = payoutCents.value - totalBetPaidCents.value
  if (net > 0) return `+$${(net / 100).toFixed(2)}`
  if (net < 0) return `-$${(Math.abs(net) / 100).toFixed(2)}`
  return 'Push'
})

const naturalLabel = computed(() => {
  if (roundResult.value?.is_natural) return 'Natural!'
  return null
})

// ─── Zone helpers ─────────────────────────────────────────────────────────────

function zoneTotal(zone: BetZone): number {
  return zoneBets.value.get(zone) ?? 0
}

function formatZoneAmount(cents: number): string {
  if (cents >= 100_000) return `${(cents / 100_000).toFixed(0)}k`
  if (cents >= 10_000) return `${(cents / 100).toFixed(0)}`
  return `${(cents / 100).toFixed(0)}`
}

// ─── Chip-placement functions ─────────────────────────────────────────────────

function placeChipOnZone(zone: BetZone): void {
  if (isDealing.value || roundDone.value) return

  const maxForZone = ZONE_CONFIGS[zone].max
  const current = zoneBets.value.get(zone) ?? 0
  const newAmount = current + selectedChipCents.value

  if (newAmount > maxForZone) {
    errorMsg.value = `Max bet for ${ZONE_CONFIGS[zone].label} is $${maxForZone / 100}`
    return
  }

  const map = new Map(zoneBets.value)
  map.set(zone, newAmount)
  zoneBets.value = map
  betHistory.value.push({ zone, chipCents: selectedChipCents.value })
  errorMsg.value = null
}

function undoLastBet(): void {
  if (betHistory.value.length === 0) return

  const last = betHistory.value.pop()!
  const map = new Map(zoneBets.value)
  const current = map.get(last.zone) ?? 0
  const newAmount = current - last.chipCents

  if (newAmount <= 0) {
    map.delete(last.zone)
  } else {
    map.set(last.zone, newAmount)
  }

  zoneBets.value = map
  errorMsg.value = null
}

function clearBets(): void {
  zoneBets.value = new Map()
  betHistory.value = []
  errorMsg.value = null
}

function rebet(): void {
  if (savedBets.value.size === 0) return
  zoneBets.value = new Map(savedBets.value)
  betHistory.value = []
  errorMsg.value = null
}

function clearHistory(): void {
  history.value = []
  stats.value = { playerWins: 0, bankerWins: 0, ties: 0, total: 0 }
}

// ─── Card helpers ─────────────────────────────────────────────────────────────

function rankLabel(rank: number): string {
  switch (rank) {
    case 1:
      return 'A'
    case 11:
      return 'J'
    case 12:
      return 'Q'
    case 13:
      return 'K'
    default:
      return String(rank)
  }
}

function suitSymbol(suit: string): string {
  switch (suit) {
    case 'hearts':
      return '♥'
    case 'diamonds':
      return '♦'
    case 'clubs':
      return '♣'
    case 'spades':
      return '♠'
    default:
      return suit
  }
}

function isRedSuit(suit: string): boolean {
  return suit === 'hearts' || suit === 'diamonds'
}

// ─── Outcome helpers ──────────────────────────────────────────────────────────

function outcomeLabel(outcome: string): string {
  switch (outcome) {
    case 'player':
      return 'Player'
    case 'banker':
      return 'Banker'
    case 'tie':
      return 'Tie'
    default:
      return outcome
  }
}

function outcomeInitial(outcome: string): string {
  switch (outcome) {
    case 'player':
      return 'P'
    case 'banker':
      return 'B'
    case 'tie':
      return 'T'
    default:
      return '?'
  }
}

// ─── Delay helper ─────────────────────────────────────────────────────────────

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

// ─── Squeeze helpers ──────────────────────────────────────────────────────────

/**
 * Runs the squeeze animation for durationMs, polling every 100ms for a skip signal.
 * Clears all masks after the animation completes or is skipped.
 */
async function runSqueeze(durationMs: number): Promise<void> {
  squeezeAnimating.value = true
  squeezeSkipped.value = false

  const steps = Math.ceil(durationMs / 100)
  for (let i = 0; i < steps; i++) {
    if (squeezeSkipped.value) {
      await delay(280) // Allow the CSS skip transition to complete
      break
    }
    await delay(100)
  }

  // Clear all masks and reset state
  squeezePlayerMasked.value = []
  squeezeBankerMasked.value = []
  squeezeAnimating.value = false
  squeezeSkipped.value = false
}

/** Called when the player clicks to skip the squeeze animation. */
function skipSqueeze(): void {
  if (squeezeAnimating.value) {
    squeezeSkipped.value = true
  }
}

// ─── Play round ───────────────────────────────────────────────────────────────

async function placeBet(): Promise<void> {
  if (!canDeal.value) return

  errorMsg.value = null

  isDealing.value = true
  roundDone.value = false
  resultVisible.value = false
  roundResult.value = null
  animPlayerCards.value = []
  animBankerCards.value = []
  playerValue.value = 0
  bankerValue.value = 0
  winnerSide.value = null
  payoutCents.value = 0

  // Build bets array from chip-placement state
  const betsArray = [...zoneBets.value.entries()].map(([type, amount_cents]) => ({
    type,
    amount_cents,
  }))
  const totalDollars = totalBetCents.value / 100
  totalBetPaidCents.value = totalBetCents.value

  try {
    const response = await apiClient.post<{
      payout: number
      multiplier: number
      is_win: boolean
      outcome_data: {
        player_cards: Card[]
        banker_cards: Card[]
        player_value: number
        banker_value: number
        outcome: 'player' | 'banker' | 'tie'
        is_natural: boolean
        player_third_card: Card | null
        banker_third_card: Card | null
      }
    }>('/api/games/baccarat/play', {
      amount: totalDollars.toFixed(2),
      options: { bets: betsArray },
    })

    // Save bets for rebet (after successful call)
    savedBets.value = new Map(zoneBets.value)

    const data = response.data
    const od = data.outcome_data

    // Store result for overlay
    roundResult.value = {
      outcome: od.outcome,
      player_value: od.player_value,
      banker_value: od.banker_value,
      is_natural: od.is_natural,
      player_cards: od.player_cards,
      banker_cards: od.banker_cards,
      player_third_card: od.player_third_card,
      banker_third_card: od.banker_third_card,
    }
    payoutCents.value = Math.round(data.payout * 100)

    // Animate card dealing: P1, B1, P2, B2
    const allPlayerCards = od.player_cards
    const allBankerCards = od.banker_cards

    // Deal initial 4 cards in interleaved order
    if (squeezeEnabled.value) {
      // Deal cards face-down: apply mask before each card appears so it arrives back-up
      squeezePlayerMasked.value = [0]
      animPlayerCards.value = [allPlayerCards[0]]
      await delay(DEAL_DELAY_MS)

      squeezeBankerMasked.value = [0]
      animBankerCards.value = [allBankerCards[0]]
      await delay(DEAL_DELAY_MS)

      squeezePlayerMasked.value = [0, 1]
      animPlayerCards.value = [allPlayerCards[0], allPlayerCards[1]]
      await delay(DEAL_DELAY_MS)

      squeezeBankerMasked.value = [0, 1]
      animBankerCards.value = [allBankerCards[0], allBankerCards[1]]

      // Brief settling pause, then start squeeze animation simultaneously on all 4 cards
      await delay(200)
      await runSqueeze(2000)
    } else {
      animPlayerCards.value = [allPlayerCards[0]]
      await delay(DEAL_DELAY_MS)
      animBankerCards.value = [allBankerCards[0]]
      await delay(DEAL_DELAY_MS)
      animPlayerCards.value = [allPlayerCards[0], allPlayerCards[1]]
      await delay(DEAL_DELAY_MS)
      animBankerCards.value = [allBankerCards[0], allBankerCards[1]]
    }

    // Update hand values after initial deal (or after squeeze reveal)
    playerValue.value = computeHandValue([allPlayerCards[0], allPlayerCards[1]])
    bankerValue.value = computeHandValue([allBankerCards[0], allBankerCards[1]])

    await delay(THIRD_CARD_DELAY_MS)

    // Animate third cards if applicable
    if (od.player_third_card !== null) {
      if (squeezeEnabled.value) squeezePlayerMasked.value = [2]
      animPlayerCards.value = [...animPlayerCards.value, od.player_third_card]
      if (squeezeEnabled.value) {
        await delay(200)
        await runSqueeze(2000)
      }
      playerValue.value = od.player_value
      await delay(DEAL_DELAY_MS)
    }

    if (od.banker_third_card !== null) {
      if (squeezeEnabled.value) squeezeBankerMasked.value = [2]
      animBankerCards.value = [...animBankerCards.value, od.banker_third_card]
      if (squeezeEnabled.value) {
        await delay(200)
        await runSqueeze(2000)
      }
      bankerValue.value = od.banker_value
      await delay(DEAL_DELAY_MS)
    }

    // Final values
    playerValue.value = od.player_value
    bankerValue.value = od.banker_value

    // Highlight winner
    roundDone.value = true
    winnerSide.value = od.outcome

    await delay(400)
    resultVisible.value = true

    // Update history
    history.value.push({
      outcome: od.outcome,
      player_value: od.player_value,
      banker_value: od.banker_value,
    })
    if (history.value.length > 100) history.value.shift()

    // Update stats
    stats.value.total++
    if (od.outcome === 'player') stats.value.playerWins++
    else if (od.outcome === 'banker') stats.value.bankerWins++
    else stats.value.ties++

    // Refresh wallet balance
    walletStore.applyGameResult(totalDollars, data.payout)
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } }
    errorMsg.value = e.response?.data?.message ?? 'An error occurred. Please try again.'
    isDealing.value = false
    return
  }

  isDealing.value = false
}

function computeHandValue(cards: Card[]): number {
  let sum = 0
  for (const card of cards) {
    sum += card.rank >= 10 ? 0 : card.rank
  }
  return sum % 10
}

function resetRound(): void {
  resultVisible.value = false
  roundResult.value = null
  roundDone.value = false
  winnerSide.value = null
  animPlayerCards.value = []
  animBankerCards.value = []
  playerValue.value = 0
  bankerValue.value = 0
  // Clear any lingering squeeze state
  squeezePlayerMasked.value = []
  squeezeBankerMasked.value = []
  squeezeAnimating.value = false
  squeezeSkipped.value = false
  // Bets persist for next round (player can rebet or modify)
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

onMounted(() => {
  if (!walletStore.balance) walletStore.fetchBalance()
})
</script>

<style scoped>
/* ── Layout ──────────────────────────────────────────────────────────────────── */
.bacc-game {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.game-topbar {
  height: 48px;
  background: var(--bg-secondary, #1a2c38);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  padding: 0 16px;
  gap: 12px;
  flex-shrink: 0;
}

.btn-back {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 8px;
  background: var(--bg-tertiary, #213743);
  transition: all 150ms ease;
  border: none;
  cursor: pointer;
  font-family: inherit;
}
.btn-back:hover {
  color: var(--text-primary, #fff);
  background: var(--bg-hover, #2a4455);
}

.game-title-bar {
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
}

.game-tag {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  padding: 2px 8px;
  border-radius: 9999px;
}

.bacc-tag {
  background: rgba(255, 215, 0, 0.1);
  color: var(--accent-gold, #ffd700);
  border: 1px solid rgba(255, 215, 0, 0.2);
}

.game-topbar-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
}

.icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--bg-tertiary, #213743);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;
  color: var(--text-secondary, #b1bad3);
  transition: all 150ms ease;
  border: none;
}
.icon-btn:hover {
  background: var(--bg-hover, #2a4455);
  color: var(--text-primary, #fff);
}

/* ── Game Body ────────────────────────────────────────────────────────────── */
.game-body {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* ── Canvas Area ──────────────────────────────────────────────────────────── */
.bacc-canvas-area {
  flex: 1;
  background: var(--bg-game, #0d1b2a);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.canvas-bg-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(0, 231, 1, 0.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 231, 1, 0.025) 1px, transparent 1px);
  background-size: 40px 40px;
  pointer-events: none;
}

.commission-badge {
  position: absolute;
  top: 10px;
  left: 14px;
  font-size: 10px;
  font-family: var(--font-mono, monospace);
  color: rgba(255, 255, 255, 0.35);
  z-index: 1;
  pointer-events: none;
}

/* ── Felt Table ───────────────────────────────────────────────────────────── */
.bacc-felt {
  width: 100%;
  max-width: 640px;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: radial-gradient(ellipse at center, #0d3320 0%, #0a2618 60%, var(--bg-game, #0d1b2a) 100%);
}

/* ── Zones ────────────────────────────────────────────────────────────────── */
.bacc-zone {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px 24px;
  transition:
    background 400ms ease,
    box-shadow 400ms ease;
  border-radius: 12px;
  margin: 6px;
}

.bacc-zone.winner {
  box-shadow: 0 0 40px rgba(0, 231, 1, 0.2);
}

.banker-zone.winner {
  background: rgba(237, 66, 69, 0.08);
  box-shadow: 0 0 40px rgba(237, 66, 69, 0.2);
}

.player-zone.winner {
  background: rgba(20, 117, 225, 0.08);
  box-shadow: 0 0 40px rgba(20, 117, 225, 0.2);
}

.zone-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.zone-icon {
  font-size: 18px;
}

.zone-label {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.6);
}

.hand-value-badge {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--bg-tertiary, #213743);
  border: 2px solid rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-display, sans-serif);
  font-size: 15px;
  font-weight: 900;
  transition: all 300ms ease;
}

.banker-badge {
  border-color: rgba(237, 66, 69, 0.4);
  color: var(--color-danger-light, #f87171);
}

.player-badge {
  border-color: rgba(20, 117, 225, 0.4);
  color: var(--color-info-light, #60a5fa);
}

.winner .hand-value-badge {
  background: var(--accent-primary, #00e701);
  color: var(--color-on-accent, #000);
  border-color: var(--accent-primary, #00e701);
}

.banker-zone.winner .hand-value-badge {
  background: var(--color-danger, #ed4245);
  color: var(--color-on-dark, #fff);
  border-color: var(--color-danger, #ed4245);
}

.player-zone.winner .hand-value-badge {
  background: var(--accent-secondary, #1475e1);
  color: var(--color-on-dark, #fff);
  border-color: var(--accent-secondary, #1475e1);
}

/* ── Cards Area ───────────────────────────────────────────────────────────── */
.cards-area {
  min-height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cards-row {
  display: flex;
  gap: 10px;
  align-items: center;
  min-height: 90px;
}

/* ── Playing Card ─────────────────────────────────────────────────────────── */
.playing-card {
  width: 60px;
  height: 84px;
  border-radius: 8px;
  background: var(--color-on-dark, #fff);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 4px;
  position: relative;
  transition: transform 200ms ease;
  flex-shrink: 0;
}

.playing-card.third-card {
  transform: rotate(-5deg);
  margin-left: 4px;
}

.playing-card.red {
  color: var(--color-danger, #dc2626);
}

.playing-card.black {
  color: var(--color-on-dark, #111);
}

.card-corner {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.1;
}

.card-corner.br {
  align-items: flex-end;
  transform: rotate(180deg);
}

.card-rank {
  font-size: 13px;
  font-weight: 800;
  line-height: 1;
}

.card-suit-small {
  font-size: 9px;
  line-height: 1;
}

.card-center-suit {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 24px;
  opacity: 0.85;
}

/* ── Card Deal Animation ──────────────────────────────────────────────────── */
.card-deal-enter-active {
  transition:
    transform 350ms cubic-bezier(0.34, 1.56, 0.64, 1),
    opacity 250ms ease;
}

.card-deal-enter-from {
  opacity: 0;
  transform: translateY(-60px) rotateY(90deg) scale(0.8);
}

/* ── Betting Table ────────────────────────────────────────────────────────── */
.betting-table {
  display: flex;
  height: 88px;
  flex-shrink: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.2);
  transition: opacity 200ms ease;
}

.betting-table.disabled {
  opacity: 0.45;
  pointer-events: none;
}

.bet-zone {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-right: 1px solid rgba(255, 255, 255, 0.06);
  cursor: pointer;
  transition: background 120ms ease;
  padding: 6px 4px;
  position: relative;
}

.bet-zone:last-child {
  border-right: none;
}

.bet-zone:hover {
  background: rgba(255, 255, 255, 0.04);
}

.bet-zone.funded {
  background: rgba(255, 255, 255, 0.03);
}

/* Zone-specific accent colors on hover */
.zone-player:hover,
.zone-player.funded {
  background: rgba(20, 117, 225, 0.07);
}

.zone-banker:hover,
.zone-banker.funded {
  background: rgba(237, 66, 69, 0.07);
}

.zone-tie:hover,
.zone-tie.funded {
  background: rgba(0, 231, 1, 0.07);
}

.zone-player-pair:hover,
.zone-player-pair.funded {
  background: rgba(20, 117, 225, 0.05);
}

.zone-banker-pair:hover,
.zone-banker-pair.funded {
  background: rgba(237, 66, 69, 0.05);
}

.bz-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
}

.bz-label {
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: rgba(255, 255, 255, 0.75);
}

.bz-odds {
  font-size: 9px;
  font-family: var(--font-mono, monospace);
  color: rgba(255, 255, 255, 0.4);
}

.bz-chip-area {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  width: 36px;
}

.bz-chip {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(255, 255, 255, 0.35);
  font-family: var(--font-mono, monospace);
  font-size: 9px;
  font-weight: 800;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
}

.chip-color-blue {
  background: linear-gradient(135deg, #1475e1, #0d5abf);
  color: var(--color-on-dark, #fff);
}

.chip-color-red {
  background: linear-gradient(135deg, #ed4245, #c0353a);
  color: var(--color-on-dark, #fff);
}

.chip-color-green {
  background: linear-gradient(135deg, #00e701, #00aa00);
  color: var(--color-on-accent, #000);
}

.chip-color-blue-light {
  background: linear-gradient(135deg, #38bdf8, #0ea5e9);
  color: var(--color-on-accent, #000);
}

.chip-color-red-light {
  background: linear-gradient(135deg, #f87171, #ef4444);
  color: var(--color-on-dark, #fff);
}

.bz-empty-ring {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px dashed rgba(255, 255, 255, 0.18);
}

/* Chip pop animation */
.chip-pop-enter-active {
  transition:
    transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1),
    opacity 150ms ease;
}

.chip-pop-enter-from {
  opacity: 0;
  transform: scale(0.5);
}

/* ── Road Strip ───────────────────────────────────────────────────────────── */
.road-strip {
  display: flex;
  gap: 4px;
  padding: 5px 16px;
  overflow-x: auto;
  background: rgba(0, 0, 0, 0.25);
  flex-shrink: 0;
  min-height: 32px;
  align-items: center;
}

.road-dot {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 800;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.road-dot.player {
  background: var(--accent-secondary, #1475e1);
  color: var(--color-on-dark, #fff);
}

.road-dot.banker {
  background: var(--color-danger, #ed4245);
  color: var(--color-on-dark, #fff);
}

.road-dot.tie {
  background: var(--color-success, #00e701);
  color: var(--color-on-accent, #000);
}

/* ── Result Overlay ───────────────────────────────────────────────────────── */
.result-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: rgba(10, 22, 35, 0.88);
  backdrop-filter: blur(6px);
  z-index: 20;
}

.result-text {
  font-family: var(--font-display, sans-serif);
  font-size: 36px;
  letter-spacing: 0.04em;
  animation: winPop 0.4s ease both;
}

.result-text.player {
  color: var(--color-info-light, #60a5fa);
  text-shadow: 0 0 40px rgba(20, 117, 225, 0.6);
}

.result-text.banker {
  color: var(--color-danger-light, #f87171);
  text-shadow: 0 0 40px rgba(237, 66, 69, 0.6);
}

.result-text.tie {
  color: var(--accent-gold, #ffd700);
  text-shadow: 0 0 40px rgba(255, 215, 0, 0.5);
}

.result-scores {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 15px;
}

.score-badge {
  padding: 4px 14px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.08);
  font-family: var(--font-mono, monospace);
  font-size: 14px;
  font-weight: 700;
}

.score-badge.win {
  background: rgba(0, 231, 1, 0.15);
  color: var(--accent-primary, #00e701);
  border: 1px solid rgba(0, 231, 1, 0.3);
}

.score-vs {
  color: rgba(255, 255, 255, 0.4);
  font-size: 12px;
}

.result-amount {
  font-family: var(--font-mono, monospace);
  font-size: 26px;
  font-weight: 700;
}

.result-amount.win {
  color: var(--color-success, #00e701);
}

.result-amount.loss {
  color: var(--color-danger, #ed4245);
}

.result-natural {
  font-size: 13px;
  color: var(--accent-gold, #ffd700);
  background: rgba(255, 215, 0, 0.1);
  padding: 4px 14px;
  border-radius: 20px;
  border: 1px solid rgba(255, 215, 0, 0.25);
}

.btn-play-again {
  padding: 12px 32px;
  background: var(--accent-primary, #00e701);
  color: var(--color-on-accent, #000);
  font-size: 14px;
  font-weight: 800;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 150ms ease;
  font-family: inherit;
  margin-top: 4px;
}

.btn-play-again:hover {
  background: var(--accent-primary-hover, #00cc01);
  transform: translateY(-1px);
  box-shadow: 0 0 20px rgba(0, 231, 1, 0.4);
}

/* ── Result Fade Transition ───────────────────────────────────────────────── */
.result-fade-enter-active {
  transition: opacity 300ms ease;
}

.result-fade-enter-from {
  opacity: 0;
}

/* ── Sidebar ──────────────────────────────────────────────────────────────── */
.game-sidebar {
  width: 280px;
  background: var(--bg-secondary, #1a2c38);
  border-left: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
}

.game-tabs {
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}

.game-tab {
  flex: 1;
  padding: 13px 4px;
  font-size: 11px;
  font-weight: 700;
  color: var(--text-tertiary, #7f8fa4);
  text-align: center;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 150ms ease;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: none;
  border-top: none;
  border-left: none;
  border-right: none;
  font-family: inherit;
}

.game-tab.active {
  color: var(--accent-primary, #00e701);
  border-bottom-color: var(--accent-primary, #00e701);
}

.tab-content {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* ── Chip Row ─────────────────────────────────────────────────────────────── */
.ctrl-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 8px;
  display: block;
}

.chip-row {
  display: flex;
  gap: 5px;
  margin-bottom: 10px;
}

.chip {
  flex: 1;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 800;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 150ms ease;
  flex-shrink: 0;
  font-family: inherit;
  padding: 0;
}

.chip:hover,
.chip.selected {
  transform: scale(1.15) translateY(-2px);
  border-color: rgba(255, 255, 255, 0.5);
  box-shadow: 0 0 14px rgba(0, 0, 0, 0.6);
}

.chip:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
}

.chip-1 {
  background: linear-gradient(135deg, #4a4a4a, #2a2a2a);
  color: var(--color-on-dark, #fff);
}

.chip-5 {
  background: linear-gradient(135deg, #1475e1, #0d5abf);
  color: var(--color-on-dark, #fff);
}

.chip-25 {
  background: linear-gradient(135deg, #00e701, #00aa00);
  color: var(--color-on-accent, #000);
}

.chip-100 {
  background: linear-gradient(135deg, #ffd700, #cc9900);
  color: var(--color-on-accent, #000);
}

.chip-500 {
  background: linear-gradient(135deg, #9b59b6, #7d3f9a);
  color: var(--color-on-dark, #fff);
}

/* ── Action Buttons Row ───────────────────────────────────────────────────── */
.bet-action-row {
  display: flex;
  gap: 5px;
  margin-bottom: 4px;
}

.btn-bet-action {
  flex: 1;
  padding: 7px 4px;
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  font-size: 11px;
  font-weight: 700;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition: all 150ms ease;
  font-family: inherit;
  text-align: center;
}

.btn-bet-action:hover:not(:disabled) {
  background: var(--bg-hover, #2a4455);
  color: var(--text-primary, #fff);
}

.btn-bet-action:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

/* ── Divider ──────────────────────────────────────────────────────────────── */
.ctrl-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
  margin: 10px 0;
}

/* ── Zone Bet List ────────────────────────────────────────────────────────── */
.zone-bet-list {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-bottom: 10px;
}

.zone-bet-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  border-radius: 6px;
  background: var(--bg-tertiary, #213743);
  border: 1px solid transparent;
  transition: all 150ms ease;
}

.zone-bet-row.funded {
  border-color: rgba(255, 255, 255, 0.08);
}

.zbr-player.funded {
  border-color: rgba(20, 117, 225, 0.25);
  background: rgba(20, 117, 225, 0.08);
}

.zbr-banker.funded {
  border-color: rgba(237, 66, 69, 0.25);
  background: rgba(237, 66, 69, 0.08);
}

.zbr-tie.funded {
  border-color: rgba(0, 231, 1, 0.25);
  background: rgba(0, 231, 1, 0.06);
}

.zbr-player-pair.funded,
.zbr-banker-pair.funded {
  border-color: rgba(255, 255, 255, 0.12);
}

.zbr-name {
  font-size: 11px;
  font-weight: 700;
  color: var(--text-secondary, #b1bad3);
  min-width: 52px;
}

.zbr-odds {
  font-size: 10px;
  font-family: var(--font-mono, monospace);
  color: var(--text-tertiary, #7f8fa4);
  flex: 1;
}

.zbr-amount {
  font-family: var(--font-mono, monospace);
  font-size: 11px;
  font-weight: 700;
  color: var(--text-tertiary, #7f8fa4);
  text-align: right;
}

.zbr-amount.active {
  color: var(--accent-primary, #00e701);
}

/* ── Limit Bar ────────────────────────────────────────────────────────────── */
.limit-bar {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 9px;
  color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.lb-item {
  font-family: var(--font-mono, monospace);
}

.lb-sep {
  opacity: 0.4;
}

/* ── Stat Pills ───────────────────────────────────────────────────────────── */
.stat-pills {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  margin-bottom: 10px;
}

.stat-pill {
  background: var(--bg-tertiary, #213743);
  border-radius: 8px;
  padding: 8px 10px;
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.sp-label {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 3px;
}

.sp-value {
  font-family: var(--font-mono, monospace);
  font-size: 13px;
  font-weight: 700;
  color: var(--accent-primary, #00e701);
}

.sp-value.gold {
  color: var(--accent-gold, #ffd700);
}

/* ── Warning & Error ──────────────────────────────────────────────────────── */
.warn-msg {
  font-size: 12px;
  color: var(--color-warning, #ffa726);
  margin-bottom: 8px;
  padding: 6px 10px;
  background: rgba(255, 167, 38, 0.08);
  border-radius: 6px;
  border: 1px solid rgba(255, 167, 38, 0.2);
}

.error-msg {
  font-size: 12px;
  color: var(--color-danger, #ed4245);
  margin-bottom: 8px;
  padding: 6px 10px;
  background: rgba(237, 66, 69, 0.1);
  border-radius: 6px;
  border: 1px solid rgba(237, 66, 69, 0.2);
}

/* ── Action Button ────────────────────────────────────────────────────────── */
.btn-action {
  width: 100%;
  padding: 14px;
  background: var(--accent-primary, #00e701);
  color: var(--color-on-accent, #000);
  font-size: 15px;
  font-weight: 800;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 150ms ease;
  letter-spacing: 0.03em;
  margin-bottom: 8px;
  font-family: inherit;
}

.btn-action:hover:not(:disabled) {
  background: var(--accent-primary-hover, #00cc01);
  box-shadow: 0 0 20px rgba(0, 231, 1, 0.4);
  transform: translateY(-1px);
}

.btn-action:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

/* ── Balance Row ──────────────────────────────────────────────────────────── */
.balance-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  margin-top: 4px;
}

.bal-label {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
}

.bal-value {
  font-family: var(--font-mono, monospace);
  font-size: 13px;
  font-weight: 700;
  color: var(--text-secondary, #b1bad3);
}

.bal-value.green {
  color: var(--color-success, #00e701);
}

/* ── History Tab ──────────────────────────────────────────────────────────── */
.history-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  margin-bottom: 4px;
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 320px;
  overflow-y: auto;
}

.history-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  background: var(--bg-tertiary, #213743);
}

.history-row.player {
  border-left: 3px solid #1475e1;
}

.history-row.banker {
  border-left: 3px solid #ed4245;
}

.history-row.tie {
  border-left: 3px solid var(--accent-gold, #ffd700);
}

.hr-round {
  font-size: 10px;
  color: var(--text-tertiary, #7f8fa4);
  font-family: var(--font-mono, monospace);
  flex-shrink: 0;
}

.hr-outcome {
  font-weight: 700;
  flex: 1;
}

.hr-scores {
  font-family: var(--font-mono, monospace);
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
}

.history-empty {
  text-align: center;
  padding: 20px;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 13px;
}

/* ── Rules Tab ────────────────────────────────────────────────────────────── */
.rules-section {
  margin-bottom: 16px;
}

.rules-title {
  font-size: 12px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.rules-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 0;
}

.rules-item {
  font-size: 12px;
  color: var(--text-secondary, #b1bad3);
  padding-left: 14px;
  position: relative;
  line-height: 1.55;
}

.rules-item::before {
  content: '▸';
  position: absolute;
  left: 0;
  color: var(--accent-primary, #00e701);
  font-size: 10px;
  top: 1px;
}

.fair-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: rgba(20, 117, 225, 0.1);
  border: 1px solid rgba(20, 117, 225, 0.2);
  border-radius: 9999px;
  padding: 4px 10px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: var(--accent-secondary, #1475e1);
  margin-top: 12px;
  cursor: pointer;
}

/* ── Roads Tab ────────────────────────────────────────────────────────────── */
.roads-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.roads-header .ctrl-label {
  margin-bottom: 0;
}

.btn-clear-roads {
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 6px;
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  border: 1px solid rgba(255, 255, 255, 0.08);
  cursor: pointer;
  font-family: inherit;
  transition: all 150ms ease;
}

.btn-clear-roads:hover:not(:disabled) {
  background: var(--bg-hover, #2a4455);
  color: var(--text-primary, #fff);
}

.btn-clear-roads:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.road-section {
  margin-bottom: 4px;
}

.road-title {
  font-size: 11px;
  font-weight: 700;
  color: var(--text-secondary, #b1bad3);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 8px;
}

.road-subtitle {
  font-size: 9px;
  font-weight: 400;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: none;
  letter-spacing: 0;
}

/* Bead Road: 10-column grid */
.bead-road-grid {
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 2px;
}

.bead-cell {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  font-weight: 800;
  border: 1px solid rgba(255, 255, 255, 0.12);
  cursor: default;
}

.bead-cell.player {
  background: var(--accent-secondary, #1475e1);
  color: var(--color-on-dark, #fff);
}

.bead-cell.banker {
  background: var(--color-danger, #ed4245);
  color: var(--color-on-dark, #fff);
}

.bead-cell.tie {
  background: var(--color-success, #00e701);
  color: var(--color-on-accent, #000);
}

/* Big Road: expanding columns */
.big-road-container {
  display: flex;
  gap: 2px;
  overflow-x: auto;
  padding-bottom: 4px;
  min-height: 136px;
  align-items: flex-start;
}

.big-road-col {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex-shrink: 0;
}

.big-road-cell {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  font-weight: 800;
  position: relative;
  flex-shrink: 0;
  cursor: default;
}

.big-road-cell.player {
  background: transparent;
  border: 2px solid var(--accent-secondary, #1475e1);
  color: var(--accent-secondary, #1475e1);
}

.big-road-cell.banker {
  background: transparent;
  border: 2px solid var(--color-danger, #ed4245);
  color: var(--color-danger, #ed4245);
}

.br-initial {
  font-size: 8px;
  font-weight: 800;
  line-height: 1;
}

.br-tie-count {
  position: absolute;
  top: -5px;
  right: -5px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--color-success, #00e701);
  color: var(--color-on-accent, #000);
  font-size: 7px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--bg-secondary, #1a2c38);
}

.road-empty {
  text-align: center;
  padding: 12px;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 12px;
  font-style: italic;
}

/* Road Summary Stats */
.road-stats {
  display: flex;
  gap: 5px;
}

.rs-item {
  flex: 1;
  background: var(--bg-tertiary, #213743);
  border-radius: 6px;
  padding: 7px 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.rs-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  font-weight: 800;
}

.player-dot {
  background: var(--accent-secondary, #1475e1);
  color: var(--color-on-dark, #fff);
}

.banker-dot {
  background: var(--color-danger, #ed4245);
  color: var(--color-on-dark, #fff);
}

.tie-dot {
  background: var(--color-success, #00e701);
  color: var(--color-on-accent, #000);
}

.rs-label {
  font-size: 8px;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 700;
}

.rs-count {
  font-family: var(--font-mono, monospace);
  font-size: 13px;
  font-weight: 700;
  color: var(--text-primary, #fff);
}

.road-disclaimer {
  margin-top: 12px;
  font-size: 10px;
  color: var(--text-tertiary, #7f8fa4);
  line-height: 1.5;
  padding: 8px 10px;
  background: rgba(255, 167, 38, 0.05);
  border-left: 2px solid rgba(255, 167, 38, 0.3);
  border-radius: 0 4px 4px 0;
}

/* ── Squeeze Mask ─────────────────────────────────────────────────────────── */

/**
 * The squeeze mask is an absolute overlay placed on top of each card's face.
 * It uses a card-back design and slides right to reveal the face underneath.
 * - Default state: no animation (mask stays covering the card)
 * - .animating: triggers squeeze-reveal CSS animation
 * - .skip: overrides animation for instant reveal
 */
.squeeze-mask {
  position: absolute;
  inset: -1px;
  border-radius: 8px;
  z-index: 10;
  cursor: pointer;
  /* Card back: cross-hatch pattern over a dark blue gradient */
  background:
    repeating-linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.04) 0,
      rgba(255, 255, 255, 0.04) 1px,
      transparent 0,
      transparent 8px
    ),
    repeating-linear-gradient(
      -45deg,
      rgba(255, 255, 255, 0.04) 0,
      rgba(255, 255, 255, 0.04) 1px,
      transparent 0,
      transparent 8px
    ),
    linear-gradient(160deg, #1e3a5f 0%, #0d2233 45%, #1a3348 100%);
  background-size: 8px 8px, 8px 8px, 100% 100%;
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: inset 0 0 12px rgba(0, 0, 0, 0.4);
}

.squeeze-mask.animating {
  animation: squeeze-reveal 2s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

.squeeze-mask.skip {
  animation: none !important;
  transform: translateX(115%);
  transition: transform 0.25s ease-out;
}

/**
 * Squeeze reveal animation:
 *   0%   → mask fully covers card (face-down)
 *   55%  → mask slides right 85% — peek: suit visible, rank corner still hidden
 *   72%  → holds at peek so player can see the suit
 *   100% → mask slides fully off-screen right (fully revealed)
 */
@keyframes squeeze-reveal {
  0% {
    transform: translateX(0);
  }
  55% {
    transform: translateX(85%);
  }
  72% {
    transform: translateX(85%);
  }
  100% {
    transform: translateX(115%);
  }
}

/* ── Skip Squeeze Button ──────────────────────────────────────────────────── */

.btn-skip-squeeze {
  position: absolute;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 15;
  padding: 10px 28px;
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 20px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  backdrop-filter: blur(8px);
  transition: all 150ms ease;
  font-family: inherit;
  white-space: nowrap;
  letter-spacing: 0.02em;
}

.btn-skip-squeeze:hover {
  background: rgba(0, 0, 0, 0.8);
  border-color: rgba(255, 255, 255, 0.4);
  color: var(--color-on-dark, #fff);
  transform: translateX(-50%) translateY(-2px);
}

/* ── Squeeze Toggle ───────────────────────────────────────────────────────── */

.squeeze-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  margin-top: 6px;
}

.squeeze-toggle-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sq-label {
  font-size: 11px;
  font-weight: 700;
  color: var(--text-secondary, #b1bad3);
}

.sq-desc {
  font-size: 9px;
  color: var(--text-tertiary, #7f8fa4);
}

.sq-toggle {
  padding: 5px 14px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: var(--bg-tertiary, #213743);
  color: var(--text-tertiary, #7f8fa4);
  transition: all 150ms ease;
  font-family: inherit;
  flex-shrink: 0;
}

.sq-toggle.active {
  background: rgba(255, 215, 0, 0.12);
  border-color: rgba(255, 215, 0, 0.3);
  color: var(--accent-gold, #ffd700);
}

.sq-toggle:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ── Keyframes ────────────────────────────────────────────────────────────── */
@keyframes winPop {
  0% {
    transform: scale(0.8);
    opacity: 0;
  }
  60% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* ── Responsive ───────────────────────────────────────────────────────────── */
@media (max-width: 900px) {
  .game-sidebar {
    width: 240px;
  }
}

@media (max-width: 768px) {
  .game-body {
    flex-direction: column;
  }

  .bacc-canvas-area {
    flex: 1;
    min-height: 300px;
  }

  .game-sidebar {
    width: 100%;
    flex-shrink: 0;
    height: 260px;
    border-left: none;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }

  .bacc-felt {
    flex-direction: row;
    max-width: 100%;
  }

  .bacc-zone {
    padding: 10px;
  }

  .betting-table {
    flex-direction: column;
    width: 90px;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .bet-zone {
    flex: none;
    height: 50px;
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    flex-direction: row;
    justify-content: space-between;
    padding: 4px 8px;
  }

  .road-strip {
    flex-direction: column;
    width: 30px;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    align-items: center;
    padding: 6px 4px;
  }

  .playing-card {
    width: 48px;
    height: 68px;
  }

  .chip-row {
    flex-wrap: wrap;
    justify-content: center;
  }

  .chip {
    width: 38px;
    height: 38px;
    font-size: 9px;
  }
}
</style>
